import ArticleIcon from "@mui/icons-material/Article";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DataGridV2 as DataGrid,
  DataGridBaseBaseProps,
  DataGridModalEditColumn,
  DateRangePicker,
  GridColumns,
  PageHeader,
  previewFile,
  Spacer,
} from "common";
import useDownloadUrl from "common/components/Inputs/File/useDownloadUrl";
import BoatCheckboxSelector from "components/BoatCheckboxSelector/BoatCheckboxSelector";
import DateRangeSelector2 from "components/DateRangeSeletor/DateRangeSelector2";
import ProgramCheckboxSelector from "components/ProgramCheckboxSelector/ProgramCheckboxSelector";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import moment from "moment";
import { BookingDocumentItem, BookingDocumentType } from "./bookingDocumentApi";
import BookingDocumentForm from "./BookingDocumentForm";
import useBookingDocumentList from "./useBookingDocumentList";

export interface BookingDocumentListProps extends Omit<
  DataGridBaseBaseProps,
  "id"
> {
  type?: BookingDocumentType;
  showHeader?: boolean;
}

function FilePreviewRowAction({ row }: { row: BookingDocumentItem }) {
  const { downloadUrl } = useDownloadUrl(true);
  const url = downloadUrl(row.file_id);
  if (!url) return null;

  return (
    <Tooltip title="Előnézet">
      <IconButton
        size="small"
        onClick={() =>
          previewFile({
            url,
            title: row.name,
            fileName:
              row.file?.name && row.file?.extension
                ? `${row.file.name}.${row.file.extension}`
                : row.file?.name,
          })
        }
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

export default function BookingDocumentList({
  type,
  showHeader = true,
  ...props
}: BookingDocumentListProps) {
  const { params, scope, filter, onChange, gridFilter, ...service } =
    useBookingDocumentList(type);
  const { hasPermissions } = useAccess();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const columns: GridColumns<BookingDocumentItem> = [
    {
      field: "name",
      headerName: I18n.t("BookingDocument.attributes.name"),
      flex: 1,
      renderCell: params => <DataGridModalEditColumn params={params} />,
    },
    ...(!type
      ? [
          {
            field: "type",
            headerName: I18n.t("BookingDocument.attributes.type"),
          },
        ]
      : []),
    {
      field: "booking.booking_number",
      headerName: I18n.t("Booking.attributes.booking_number"),
    },
    {
      field: "booking.event.event_group.start_time",
      headerName: I18n.t("Event.attributes.start_time"),
      renderCell: (p: any) =>
        p.row?.booking?.event?.start_time
          ? moment(p.row.booking.event.start_time).format("L LT")
          : null,
      valueFormatter: (p: any) =>
        p.row?.booking?.event?.start_time
          ? moment(p.row.booking.event.start_time).format("L LT")
          : "",
    },
    {
      field: "booking.event.boat.name",
      sortable: false,
      headerName: I18n.t("Event.attributes.boat_id"),
    },
    {
      field: "booking.event.program.name",
      sortable: false,
      headerName: I18n.t("Event.attributes.program_id"),
    },
    {
      field: "booking.name",
      headerName: I18n.t("Booking.attributes.name"),
      flex: 1,
    },
    {
      field: "booking.email",
      headerName: I18n.t("Booking.attributes.email"),
      flex: 1,
      hideable: true,
    },
    {
      field: "booking.number_of_guests",
      headerName: I18n.t("Booking.attributes.number_of_guests"),
      type: "number",
      hideable: true,
    },
    {
      field: "booking.partner_name",
      headerName: I18n.t("Booking.attributes.partner_id"),
      hideable: true,
      sortable: false,
    },
  ];

  return (
    <>
      {showHeader && (
        <Box sx={{ px: smDown ? 2 : 0 }}>
          <PageHeader
            avatar={<ArticleIcon />}
            title={
              type
                ? I18n.t(`BookingDocument.types.${type}`, {
                    defaultValue: I18n.t("BookingDocument.title"),
                  })
                : I18n.t("BookingDocument.title")
            }
          >
            {smDown ? (
              <DateRangeSelector2
                numberOfCalendars={1}
                numberOfYears={1}
                value={
                  filter?.range?.start && filter?.range?.end
                    ? {
                        start: moment(filter.range.start),
                        end: moment(filter.range.end),
                      }
                    : undefined
                }
                onChange={(start, end) => {
                  onChange({
                    range:
                      start && end
                        ? {
                            start: start.format("YYYY-MM-DD"),
                            end: end.format("YYYY-MM-DD"),
                          }
                        : undefined,
                  });
                }}
                dateButtonMinWidth={150}
              />
            ) : (
              <DateRangePicker
                value={{ from: filter?.range?.start, to: filter?.range?.end }}
                defaultRangeIdStorageKey="booking_documents_default_range"
                onChange={({ from, to }) =>
                  onChange({
                    range: { start: from as any, end: to as any },
                  })
                }
              />
            )}
          </PageHeader>
          <BoatCheckboxSelector
            value={filter?.boatIds}
            color={theme.palette.primary.main}
            onChange={evt => {
              onChange({ boatIds: (evt.target.value as any) || [] });
            }}
          />
          <Spacer h={2} />
          <ProgramCheckboxSelector
            value={filter?.programIds}
            onChange={evt => {
              onChange({ programIds: (evt.target.value as any) || [] });
            }}
          />
          <Spacer h={2} />
        </Box>
      )}
      <DataGrid
        {...service}
        filter={gridFilter}
        columns={columns}
        id="booking-documents"
        renderModalForm={formProps => (
          <BookingDocumentForm
            {...formProps}
            type={type}
            defaultValues={type ? { type } : undefined}
          />
        )}
        rowActions={params => [
          <FilePreviewRowAction key="preview" row={params.row} />,
        ]}
        canCreate={!showHeader && hasPermissions("booking_document.create")}
        canDelete={hasPermissions([
          "booking_document.editany",
          "booking_document.editown",
        ])}
        canEdit={hasPermissions([
          "booking_document.editany",
          "booking_document.editown",
        ])}
        defaultState={{
          sorting: [{ field: "booking.event.start_time", direction: "asc" }],
          hidden_columns: [
            "booking.email",
            "booking.number_of_guests",
            "booking.partner_name",
          ],
        }}
        {...props}
      />
    </>
  );
}
